import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import {
  ICrearCampanaDetalleReq,
  ICrearCampanaReq,
} from '@core/interfaces/request-response/campana.dto.interface';
import { CampanaApiService } from '@core/services/api/campana-api.service';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { ProductoApiService } from '@core/services/api/producto.service';
import { SucursalApiService } from '@core/services/api/sucursal-api.service';
import { AppService } from '@shared/services/app.service';
import { PermisoService } from '@shared/services/permiso.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ToastService } from '@shared/services/toast.service';
import { UiDialog, UiSelect, type SelectOption } from '@shared/components/ui';
import {
  EEstadoCampana,
  EObjetivoCampana,
  ETipoDescuentoCampana,
  ICampanaData,
} from '@core/interfaces/entidades/campana.entidad';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';

interface INuevaCampanaForm {
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string;
  sucursal_id: number | null;
  reglas: ICrearCampanaDetalleReq[];
}

@Component({
  selector: 'app-campana',
  imports: [UiDialog, DatePipe, CommonModule, UiSelect, FormsModule],
  templateUrl: './campana.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campana.css',
})
export class Campana implements OnInit {
  private readonly appService = inject(AppService);
  private readonly toastService = inject(ToastService);
  private readonly sucursalService = inject(SucursalService);
  private readonly permisoService = inject(PermisoService);

  private readonly campanaApiService = inject(CampanaApiService);
  private readonly categoriaApiService = inject(CategoriaApiService);
  private readonly productoApiService = inject(ProductoApiService);
  private readonly sucursalApiService = inject(SucursalApiService);

  private readonly sucursalContextId = signal<number | null>(null);

  readonly TABS: EEstadoCampana[] = Object.values(EEstadoCampana);
  readonly ESTADOS = EEstadoCampana;
  readonly TIPOS_OBJETIVO: EObjetivoCampana[] = Object.values(EObjetivoCampana);
  readonly TIPOS_DESCUENTO: ETipoDescuentoCampana[] = Object.values(ETipoDescuentoCampana);

  tabSeleccionado = signal<EEstadoCampana>(EEstadoCampana.ACTIVA);
  modalNuevaCampana = signal(false);

  campanas = signal<ICampanaData[]>([]);
  categorias = signal<ICategoriaData[]>([]);
  productos = signal<IProductoData[]>([]);
  sucursales = signal<ISucursalData[]>([]);

  sucursalesOptions = computed<SelectOption[]>(() =>
    this.sucursales().map((s) => ({ label: s.nombre, value: s.id })),
  );
  productosOptions = computed<SelectOption[]>(() =>
    this.productos().map((p) => ({ label: p.nombre, value: p.id })),
  );
  categoriasOptions = computed<SelectOption[]>(() =>
    this.categorias().map((c) => ({ label: c.nombre, value: c.id })),
  );
  tiposObjetivoOptions = computed<SelectOption[]>(() =>
    this.TIPOS_OBJETIVO.map((t) => ({ label: t, value: t })),
  );
  tiposDescuentoOptions = computed<SelectOption[]>(() =>
    this.TIPOS_DESCUENTO.map((t) => ({ label: t, value: t })),
  );

  nuevaCampana = signal<INuevaCampanaForm>(this.buildNuevaCampanaForm());

  esAdministrador = signal(false);

  campanasFiltradas = computed(() => {
    const tab = this.tabSeleccionado();
    return this.campanas().filter((campana) => this.normalizarEstado(campana.estado) === tab);
  });

  totalesPorEstado = computed(() => {
    const initial = {
      ACTIVA: 0,
      PAUSADA: 0,
      FINALIZADA: 0,
    };

    return this.campanas().reduce(
      (acc, campana) => {
        const estado = this.normalizarEstado(campana.estado);
        acc[estado] += 1;
        return acc;
      },
      { ...initial },
    );
  });

  constructor() {
    effect(
      () => {
        if (!this.sucursalService.sucursalReady() || !this.appService.authReady()) return;

        const activeSucursalId = this.sucursalService.activeSucursal()?.id ?? null;
        if (this.sucursalContextId() === activeSucursalId) return;

        this.sucursalContextId.set(activeSucursalId);
        this.nuevaCampana.set(this.buildNuevaCampanaForm(activeSucursalId));
        this.cargarCatalogos();
        this.cargarCampanas();
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Campañas',
      subtitle: 'Gestiona campañas activas, pausadas y finalizadas para productos o categorías.',
      icon: 'bi-megaphone',
      showBackButton: true,
    });

    this.permisoService.permisos$.subscribe((permisos) => {
      this.esAdministrador.set(permisos.some((p) => p === ETipoPermiso.ADMINISTRADOR));
    });
  }

  abrirModalNuevaCampana() {
    this.nuevaCampana.set(this.buildNuevaCampanaForm(this.sucursalContextId()));
    this.modalNuevaCampana.set(true);
  }

  cerrarModalNuevaCampana() {
    this.modalNuevaCampana.set(false);
  }

  agregarRegla() {
    this.nuevaCampana.update((form) => ({
      ...form,
      reglas: [
        ...form.reglas,
        {
          objetivo_tipo: EObjetivoCampana.PRODUCTO,
          producto_id: null,
          categoria_id: null,
          tipo_descuento: ETipoDescuentoCampana.PORCENTAJE,
          valor_descuento: 0,
          precio_minimo: 0,
          prioridad: 0,
        },
      ],
    }));
  }

  eliminarRegla(index: number) {
    this.nuevaCampana.update((form) => ({
      ...form,
      reglas: form.reglas.filter((_, i) => i !== index),
    }));
  }

  onNombreChange(value: string) {
    this.nuevaCampana.update((form) => ({ ...form, nombre: value }));
  }

  onFechaInicioChange(value: string) {
    this.nuevaCampana.update((form) => ({ ...form, fecha_inicio: value }));
  }

  onFechaFinChange(value: string) {
    this.nuevaCampana.update((form) => ({ ...form, fecha_fin: value }));
  }

  onSucursalChange(value: string | number | null) {
    const parsed = value === '' || value === null ? null : Number(value);
    this.nuevaCampana.update((form) => ({
      ...form,
      sucursal_id: Number.isNaN(parsed) ? null : parsed,
    }));
  }

  onReglaObjetivoTipoChange(index: number, value: EObjetivoCampana) {
    this.actualizarRegla(index, {
      objetivo_tipo: value,
      producto_id:
        value === EObjetivoCampana.PRODUCTO ? this.nuevaCampana().reglas[index].producto_id : null,
      categoria_id:
        value === EObjetivoCampana.CATEGORIA
          ? this.nuevaCampana().reglas[index].categoria_id
          : null,
    });
  }

  onReglaProductoChange(index: number, value: string | number | null) {
    const producto_id = value === '' || value === null ? null : Number(value);
    this.actualizarRegla(index, {
      producto_id: Number.isNaN(producto_id) ? null : producto_id,
    });
  }

  onReglaCategoriaChange(index: number, value: string | number | null) {
    const categoria_id = value === '' || value === null ? null : Number(value);
    this.actualizarRegla(index, {
      categoria_id: Number.isNaN(categoria_id) ? null : categoria_id,
    });
  }

  onReglaTipoDescuentoChange(index: number, value: ETipoDescuentoCampana) {
    this.actualizarRegla(index, { tipo_descuento: value });
  }

  onReglaValorDescuentoChange(index: number, value: string) {
    const valor_descuento = Number(value);
    this.actualizarRegla(index, {
      valor_descuento: Number.isNaN(valor_descuento) ? 0 : valor_descuento,
    });
  }

  onReglaPrecioMinimoChange(index: number, value: string) {
    const precio_minimo = Number(value);
    this.actualizarRegla(index, {
      precio_minimo: Number.isNaN(precio_minimo) ? 0 : precio_minimo,
    });
  }

  crearCampana() {
    const form = this.nuevaCampana();
    const errores = this.validarFormulario(form);

    if (errores.length > 0) {
      this.toastService.show('top-right', 'error', 'Formulario inválido', errores.join(' | '));
      return;
    }

    const payload: ICrearCampanaReq = {
      nombre: form.nombre.trim(),
      fecha_inicio: form.fecha_inicio,
      fecha_fin: form.fecha_fin,
      sucursal_id: form.sucursal_id,
      descripcion: '',
      detalles: form.reglas.map((regla) => ({
        objetivo_tipo: regla.objetivo_tipo,
        producto_id: regla.objetivo_tipo === EObjetivoCampana.PRODUCTO ? regla.producto_id : null,
        categoria_id:
          regla.objetivo_tipo === EObjetivoCampana.CATEGORIA ? regla.categoria_id : null,
        tipo_descuento: regla.tipo_descuento,
        valor_descuento: Number(regla.valor_descuento),
        precio_minimo: Number(regla.precio_minimo),
        prioridad: regla.prioridad,
      })),
    };

    this.campanaApiService.crear(payload).subscribe({
      next: (campanaCreada) => {
        this.toastService.show(
          'top-right',
          'success',
          'Campaña creada',
          'La campaña se creó correctamente.',
        );
        this.modalNuevaCampana.set(false);
        this.campanas.update((actuales) => [campanaCreada, ...actuales]);
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error al crear campaña',
          err?.error?.message || 'No se pudo crear la campaña.',
        );
      },
    });
  }

  cambiarEstado(campanaId: number, estado: EEstadoCampana) {
    if (!this.esAdministrador()) return;

    this.campanaApiService.cambiarEstado(campanaId, { estado }).subscribe({
      next: (updated) => {
        this.campanas.update((actuales) =>
          actuales.map((campana) =>
            campana.id === campanaId
              ? { ...campana, ...updated, estado: this.normalizarEstado(updated.estado) }
              : campana,
          ),
        );
        this.toastService.show(
          'top-right',
          'success',
          'Estado actualizado',
          `La campaña quedó ${estado}.`,
        );
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error al cambiar estado',
          err?.error?.message || 'No se pudo actualizar el estado de la campaña.',
        );
      },
    });
  }

  getNombreObjetivo(regla: ICrearCampanaDetalleReq) {
    if (regla.objetivo_tipo === EObjetivoCampana.PRODUCTO) {
      return (
        this.productos().find((p) => p.id === regla.producto_id)?.nombre || 'Producto no encontrado'
      );
    }
    return (
      this.categorias().find((c) => c.id === regla.categoria_id)?.nombre ||
      'Categoría no encontrada'
    );
  }

  getNombreSucursal(campana: ICampanaData) {
    if (campana.sucursal_id == null) return 'Todas las sucursales';
    return (
      this.sucursales().find((sucursal) => sucursal.id === campana.sucursal_id)?.nombre ||
      'Sucursal no encontrada'
    );
  }

  private cargarCampanas() {
    this.campanaApiService.cargarActivas().subscribe({
      next: (data) => {
        this.campanas.set(
          data.map((campana) => ({ ...campana, estado: this.normalizarEstado(campana.estado) })),
        );
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error al listar campañas',
          err?.error?.message || 'No se pudieron cargar las campañas.',
        );
      },
    });
  }

  private cargarCatalogos() {
    this.categoriaApiService.cargar().subscribe({
      next: (categorias) => this.categorias.set(categorias),
      error: () => this.categorias.set([]),
    });

    this.cargarProductosIniciales();

    this.sucursalApiService.listar().subscribe({
      next: (sucursales) => this.sucursales.set(sucursales),
      error: () => this.sucursales.set([]),
    });
  }

  private cargarProductosIniciales() {
    this.productoApiService.paginar(1, 30).subscribe({
      next: (r) => this.productos.set(r.data),
      error: () => this.productos.set([]),
    });
  }

  buscarProductos(termino: string) {
    const t = termino?.trim() || '';
    if (t.length === 0) {
      this.cargarProductosIniciales();
      return;
    }
    this.productoApiService.buscarProducto(t).subscribe({
      next: (r) => this.productos.set(r.data),
      error: () => {},
    });
  }

  private actualizarRegla(index: number, patch: Partial<ICrearCampanaDetalleReq>) {
    this.nuevaCampana.update((form) => ({
      ...form,
      reglas: form.reglas.map((regla, i) => (i === index ? { ...regla, ...patch } : regla)),
    }));
  }

  private buildNuevaCampanaForm(defaultSucursalId: number | null = null): INuevaCampanaForm {
    return {
      nombre: '',
      fecha_inicio: '',
      fecha_fin: '',
      sucursal_id: defaultSucursalId,
      reglas: [
        {
          objetivo_tipo: EObjetivoCampana.PRODUCTO,
          producto_id: null,
          categoria_id: null,
          tipo_descuento: ETipoDescuentoCampana.PORCENTAJE,
          valor_descuento: 0,
          precio_minimo: 0,
          prioridad: 0,
        },
      ],
    };
  }

  private validarFormulario(form: INuevaCampanaForm): string[] {
    const errores: string[] = [];

    if (form.nombre.trim().length === 0) {
      errores.push('El nombre de la campaña es obligatorio.');
    }

    if (!form.fecha_inicio || !form.fecha_fin) {
      errores.push('Debes ingresar fecha de inicio y fin.');
    }

    if (form.fecha_inicio && form.fecha_fin) {
      const inicio = new Date(form.fecha_inicio);
      const fin = new Date(form.fecha_fin);
      if (inicio.getTime() > fin.getTime()) {
        errores.push('La fecha de inicio no puede ser mayor que la fecha de fin.');
      }
    }

    if (form.reglas.length === 0) {
      errores.push('Debes agregar al menos una regla de campaña.');
    }

    form.reglas.forEach((regla, index) => {
      const fila = index + 1;

      if (regla.objetivo_tipo === EObjetivoCampana.PRODUCTO && !regla.producto_id) {
        errores.push(`Regla ${fila}: selecciona un producto.`);
      }

      if (regla.objetivo_tipo === EObjetivoCampana.CATEGORIA && !regla.categoria_id) {
        errores.push(`Regla ${fila}: selecciona una categoría.`);
      }

      if (Number(regla.valor_descuento) <= 0) {
        errores.push(`Regla ${fila}: el valor del descuento debe ser mayor a 0.`);
      }

      if (
        regla.tipo_descuento === ETipoDescuentoCampana.PORCENTAJE &&
        (Number(regla.valor_descuento) <= 0 || Number(regla.valor_descuento) > 100)
      ) {
        errores.push(`Regla ${fila}: el porcentaje debe estar entre 0 y 100.`);
      }

      if (Number(regla.precio_minimo) < 0) {
        errores.push(`Regla ${fila}: el precio mínimo no puede ser negativo.`);
      }
    });

    return errores;
  }

  private normalizarEstado(estado: string | null | undefined): EEstadoCampana {
    if (estado === EEstadoCampana.PAUSADA || estado === EEstadoCampana.FINALIZADA) return estado;
    return EEstadoCampana.ACTIVA;
  }
}
